import { ErrorModal } from "@dexteel/mesf-core";
import {
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Typography,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import type { ColDef, GridApi } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { get } from "lodash-es";
import { useCallback, useEffect, useMemo, useState } from "react";
import { globalDefaultColDef } from "@setup/ag-grid/mesf-theme";
import type { TemplateRecipient } from "@setup/configurations/email-recipients/models/EmailRecipient";
import { getTemplateRecipients } from "@setup/configurations/email-recipients/repositories/EmailRecipientsRepository";

type Props = {
	show: boolean;
	templateId: number | null;
	/** Ids currently chosen in the form, which may differ from what is saved. */
	value: number[];
	onHide: (recipientIds?: number[]) => void;
};

/**
 * Same idea as the parts picker on an asset: the whole address book, with the ones already on
 * this template ticked. Nothing is written here, the ticks travel back to the template form and
 * are saved with it, so a new template can pick people before it exists.
 */
export const RecipientsPicker = ({
	show,
	templateId,
	value,
	onHide,
}: Props) => {
	const [gridApi, setGridApi] = useState<GridApi | null>(null);
	const [error, setError] = useState("");

	const { data, isLoading } = useQuery({
		queryKey: ["templateRecipients", templateId],
		queryFn: () => getTemplateRecipients(templateId),
		enabled: show,
	});

	const rows = useMemo(
		() => get(data, "data.tables[0].rows", []) as TemplateRecipient[],
		[data],
	);

	// The form is the source of truth while the modal is open, so the ticks come from `value`
	// and not from IsSelected: reopening the picker after unticking somebody must not bring
	// them back. Applied both when the rows land and when the dialog is reopened.
	const applySelection = useCallback(() => {
		if (!gridApi) return;
		gridApi.forEachNode((node) => {
			node.setSelected(value.includes(node.data?.RecipientId));
		});
	}, [gridApi, value]);

	useEffect(() => {
		if (show) applySelection();
	}, [show, applySelection]);

	const columns = useMemo<ColDef[]>(
		() => [
			{
				colId: "checkbox",
				maxWidth: 50,
				filter: false,
				sortable: false,
				checkboxSelection: true,
				headerCheckboxSelection: true,
			},
			{ field: "DisplayName", headerName: "Name", flex: 2 },
			{ field: "EmailAddress", headerName: "Email Address", flex: 3 },
		],
		[],
	);

	const handleOk = () => {
		const ids = (gridApi?.getSelectedRows() ?? []).map(
			(r: TemplateRecipient) => r.RecipientId as number,
		);
		onHide(ids);
	};

	return (
		<>
			<Dialog open={show} onClose={() => onHide()} maxWidth="md" fullWidth>
				<DialogTitle sx={{ padding: "24px 24px 0" }}>
					<Typography variant="h5" sx={{ margin: 0, fontWeight: 600 }}>
						RECIPIENTS
					</Typography>
				</DialogTitle>
				<DialogContent>
					<Typography variant="body2" sx={{ color: "#666", mb: 1 }}>
						Tick who should be emailed when this template generates work orders.
						Addresses are managed in Configuration, Email Recipients.
					</Typography>
					<div style={{ height: 420, width: "100%" }}>
						<AgGridReact
							rowData={rows}
							columnDefs={columns}
							defaultColDef={globalDefaultColDef}
							rowSelection="multiple"
							suppressRowClickSelection={false}
							loading={isLoading}
							onGridReady={(params) => setGridApi(params.api)}
							onRowDataUpdated={applySelection}
							pagination={false}
							overlayNoRowsTemplate="No addresses yet. Add them in Configuration, Email Recipients."
						/>
					</div>
				</DialogContent>
				<DialogActions
					sx={{ padding: "16px 24px 24px", justifyContent: "flex-end", gap: 2 }}
				>
					<Button
						variant="contained"
						color="inherit"
						onClick={() => onHide()}
						sx={{ minWidth: 120 }}
					>
						CANCEL
					</Button>
					<Button
						variant="contained"
						color="primary"
						onClick={handleOk}
						sx={{ minWidth: 120 }}
					>
						OK
					</Button>
				</DialogActions>
			</Dialog>
			<ErrorModal error={error} onHide={() => setError("")} />
		</>
	);
};
